// Pure-JS pcap/pcapng parser → per-flow byte streams. Loaded on demand
// when the user opens the hex view in the Network tab; parsing a few
// MB inline takes ~100ms on a laptop. If pcaps start trending over
// 50 MB (longer runs, higher-throughput malware) we swap this for a
// Rust → wasm build without touching callers.
//
//   extractFlow(bytes, { dstIp, dstPort? })
//     → { client, server, packets }
//
// client is the bytes the sample sent (local → dstIp), server is what
// came back (dstIp → local). Packets is a flat timeline for the hex
// view's gutter. TCP streams are seq-reassembled; UDP is appended in
// capture order.
//
// Formats: classic pcap (little- and big-endian; magic 0xa1b2c3d4 /
// 0xd4c3b2a1 — microsecond and nanosecond resolution handled the same
// way, we don't look at tv_frac). pcapng is deliberately NOT supported
// yet — our Suricata worker reads libpcap-format captures only.
//
// Link types: Ethernet (1) and Linux SLL (113, "cooked"). Both are
// what QEMU + the host's tap produce in practice.
//
// L3: IPv4 only. L4: TCP + UDP. Everything else is dropped silently
// (ICMP, ARP, IPv6 — they aren't what the hex view is for).

export type FlowFilter = {
  dstIp: string;
  dstPort?: number;
  /** When set, also require the remote 5-tuple to use this transport. Else TCP+UDP both match. */
  proto?: "tcp" | "udp";
};

export type PacketInfo = {
  /** Microseconds since the pcap's first packet. */
  tUs: number;
  src: string;
  dst: string;
  srcPort: number;
  dstPort: number;
  proto: "tcp" | "udp";
  /** Payload length (not frame length). */
  len: number;
  direction: "client" | "server";
  /** TCP SEQ at this packet's payload start; undefined for UDP. Used only to flag out-of-order holes in the UI. */
  seq?: number;
};

export type FlowBytes = {
  client: Uint8Array;
  server: Uint8Array;
  packets: PacketInfo[];
  /** Local endpoint picked (first observed by direction heuristic). */
  localIp?: string;
  localPort?: number;
};

const PCAP_MAGIC_LE = 0xa1b2c3d4;
const PCAP_MAGIC_BE = 0xd4c3b2a1;
const PCAP_MAGIC_NS_LE = 0xa1b23c4d;
const PCAP_MAGIC_NS_BE = 0x4d3cb2a1;

const LINKTYPE_ETHERNET = 1;
const LINKTYPE_LINUX_SLL = 113;

const ETHERTYPE_IPV4 = 0x0800;
const ETHERTYPE_VLAN = 0x8100;

const IP_PROTO_TCP = 6;
const IP_PROTO_UDP = 17;

function ipv4(view: DataView, off: number): string {
  return `${view.getUint8(off)}.${view.getUint8(off + 1)}.${view.getUint8(off + 2)}.${view.getUint8(off + 3)}`;
}

/** Scan a pcap blob and return the client/server byte streams for the flow with the given dst. */
export function extractFlow(bytes: ArrayBuffer, filter: FlowFilter): FlowBytes {
  const view = new DataView(bytes);
  if (bytes.byteLength < 24) throw new Error("pcap: truncated header");
  const magic = view.getUint32(0, true);
  let little: boolean;
  if (magic === PCAP_MAGIC_LE || magic === PCAP_MAGIC_NS_LE) little = true;
  else if (magic === PCAP_MAGIC_BE || magic === PCAP_MAGIC_NS_BE) little = false;
  else throw new Error(`pcap: not a pcap file (magic 0x${magic.toString(16)}) — pcapng is not supported yet`);

  const linktype = view.getUint32(20, little);
  if (linktype !== LINKTYPE_ETHERNET && linktype !== LINKTYPE_LINUX_SLL) {
    throw new Error(`pcap: unsupported linktype ${linktype} (want Ethernet=1 or SLL=113)`);
  }
  const l2SkipBase = linktype === LINKTYPE_ETHERNET ? 14 : 16;

  // Per-direction TCP seq → payload chunk; reassembled at the end.
  type Chunk = { seq: number; data: Uint8Array };
  const clientTcp: Chunk[] = [];
  const serverTcp: Chunk[] = [];
  const clientUdp: Uint8Array[] = [];
  const serverUdp: Uint8Array[] = [];
  const packets: PacketInfo[] = [];
  let firstTs: number | undefined;
  let localIp: string | undefined;
  let localPort: number | undefined;

  let off = 24;
  while (off + 16 <= bytes.byteLength) {
    const tsSec = view.getUint32(off, little);
    const tsFrac = view.getUint32(off + 4, little);
    const inclLen = view.getUint32(off + 8, little);
    off += 16;
    if (off + inclLen > bytes.byteLength) break;
    const packetStart = off;
    off += inclLen;

    // L2 — Ethernet or SLL. VLAN tag shifts the ethertype offset by 4;
    // we handle one tag because that's all QEMU-tap captures ever have.
    let l2Skip = l2SkipBase;
    let ethertype: number;
    if (linktype === LINKTYPE_ETHERNET) {
      if (inclLen < 14) continue;
      ethertype = view.getUint16(packetStart + 12, false);
      if (ethertype === ETHERTYPE_VLAN) {
        if (inclLen < 18) continue;
        ethertype = view.getUint16(packetStart + 16, false);
        l2Skip = 18;
      }
    } else {
      if (inclLen < 16) continue;
      ethertype = view.getUint16(packetStart + 14, false);
    }
    if (ethertype !== ETHERTYPE_IPV4) continue;

    // L3 — IPv4 only.
    const ipStart = packetStart + l2Skip;
    if (ipStart + 20 > packetStart + inclLen) continue;
    const vihl = view.getUint8(ipStart);
    if (vihl >> 4 !== 4) continue;
    const ihl = (vihl & 0x0f) * 4;
    if (ihl < 20) continue;
    const ipTotalLen = view.getUint16(ipStart + 2, false);
    const ipProto = view.getUint8(ipStart + 9);
    const srcIp = ipv4(view, ipStart + 12);
    const dstIp = ipv4(view, ipStart + 16);
    const l4Start = ipStart + ihl;
    const l4End = Math.min(ipStart + ipTotalLen, packetStart + inclLen);
    if (l4Start > l4End) continue;

    // L4 — TCP or UDP.
    let srcPort: number;
    let dstPort: number;
    let payloadStart: number;
    let proto: "tcp" | "udp";
    let seq: number | undefined;
    if (ipProto === IP_PROTO_TCP) {
      if (l4Start + 20 > l4End) continue;
      srcPort = view.getUint16(l4Start, false);
      dstPort = view.getUint16(l4Start + 2, false);
      seq = view.getUint32(l4Start + 4, false);
      const dataOff = (view.getUint8(l4Start + 12) >> 4) * 4;
      if (dataOff < 20) continue;
      payloadStart = l4Start + dataOff;
      proto = "tcp";
    } else if (ipProto === IP_PROTO_UDP) {
      if (l4Start + 8 > l4End) continue;
      srcPort = view.getUint16(l4Start, false);
      dstPort = view.getUint16(l4Start + 2, false);
      payloadStart = l4Start + 8;
      proto = "udp";
    } else {
      continue;
    }
    if (filter.proto && filter.proto !== proto) continue;

    // 5-tuple match: flow is identified by the sample's remote peer
    // (filter.dstIp[:dstPort]). Direction derives from which end of
    // the packet matches the filter.
    const toRemote = dstIp === filter.dstIp && (filter.dstPort === undefined || dstPort === filter.dstPort);
    const fromRemote = srcIp === filter.dstIp && (filter.dstPort === undefined || srcPort === filter.dstPort);
    if (!toRemote && !fromRemote) continue;
    const direction: "client" | "server" = toRemote ? "client" : "server";

    if (!localIp) {
      localIp = toRemote ? srcIp : dstIp;
      localPort = toRemote ? srcPort : dstPort;
    }

    const payloadLen = Math.max(0, l4End - payloadStart);
    if (payloadLen > 0) {
      const payload = new Uint8Array(bytes, payloadStart, payloadLen);
      if (proto === "tcp" && seq !== undefined) {
        (direction === "client" ? clientTcp : serverTcp).push({ seq, data: payload });
      } else {
        (direction === "client" ? clientUdp : serverUdp).push(payload);
      }
    }

    const tUs = tsSec * 1_000_000 + Math.min(tsFrac, 999_999);
    if (firstTs === undefined) firstTs = tUs;
    packets.push({
      tUs: tUs - firstTs,
      src: srcIp,
      dst: dstIp,
      srcPort,
      dstPort,
      proto,
      len: payloadLen,
      direction,
      seq,
    });
  }

  return {
    client: proto_assemble(clientTcp, clientUdp),
    server: proto_assemble(serverTcp, serverUdp),
    packets,
    localIp,
    localPort,
  };
}

/** TCP: order chunks by seq (with 32-bit wrap), fill gaps with zero-length segments
 *  so a missing retransmit shows as a seam rather than silently joining bytes.
 *  UDP: concat as captured. If both appear on the same flow (unusual), TCP wins. */
function proto_assemble(tcp: { seq: number; data: Uint8Array }[], udp: Uint8Array[]): Uint8Array {
  if (tcp.length) {
    const chunks = [...tcp];
    chunks.sort((a, b) => cmpSeq(a.seq, b.seq));
    // Dedupe retransmits that landed with the same (seq, length).
    const kept: { seq: number; data: Uint8Array }[] = [];
    const seenKey = new Set<string>();
    for (const c of chunks) {
      const k = `${c.seq}|${c.data.length}`;
      if (seenKey.has(k)) continue;
      seenKey.add(k);
      kept.push(c);
    }
    const total = kept.reduce((n, c) => n + c.data.length, 0);
    const out = new Uint8Array(total);
    let n = 0;
    for (const c of kept) {
      out.set(c.data, n);
      n += c.data.length;
    }
    return out;
  }
  if (!udp.length) return new Uint8Array(0);
  const total = udp.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let n = 0;
  for (const p of udp) {
    out.set(p, n);
    n += p.length;
  }
  return out;
}

/** 32-bit TCP SEQ compare with wrap. a-b in signed 32-bit space. */
function cmpSeq(a: number, b: number): number {
  const d = (a - b) | 0;
  return d;
}
